import Styles from './BrandProfileWithEditing.module.scss'
import { Formik } from 'formik'
import { BrandProfileProps } from 'components/FirstTimeExperienceModal/steps/BrandProfile/Types'

import { StickyFooterProvider } from 'providers/StickyFooterProvider/StickyFooterProvider'
import { StickyFooter, FooterButtons } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import { Button } from 'blaze-ui/Button'
import { Edit1 } from 'blaze-ui/icons'
import { useQueryClient } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Data } from 'hooks/queries/brandProfiles/useBrandProfileAnalysis'
import { AIJobStatus } from 'types'
import { usePollBrandProfileAnalysis } from 'components/FirstTimeExperienceModal/steps/BrandProfile/usePollBrandProfileAnalysis'
import { fetchStream } from 'common/Prose/nodeViews/AISuggestion/utils'
import { reportError } from 'utils/error'
import { useDeleteBrandKitSource } from 'hooks/queries/useDeleteBrandKitSource'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { useDeleteBrandProfile } from 'hooks/queries/brandProfiles/useDeleteBrandProfile'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { Heading } from 'blaze-ui/Heading/Heading'
import { makePossessiveSingular } from 'utils'

import { useUpdateBrandProfile } from 'hooks/queries/brandProfiles/useUpdateBrandProfile'
import {
  parseProfileSections,
  reconstructMarkdown,
  ProfileSection,
} from 'screens/BrandKits/components/BrandProfile/markdownUtils'
import { EditableSection } from './EditableSection'
import { useOnboardingTracking } from 'hooks/useOnboardingTracking'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { LoadingText } from './LoadingText'

const BrandProfileWithEditing = ({
  brandProfile,
  onNext,
  onBack,
  analysisId,
  initialProfile,
  deleteOnBack = true,
}: BrandProfileProps) => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const { isMobile } = useResponsiveDesign()
  const [profile, setProfile] = useState<string>(() => {
    if (initialProfile) return initialProfile
    if (brandProfile.profile instanceof Object) return ''
    return `${brandProfile.profile}`
  })
  const [refreshProfile, setRefreshProfile] = useState(false)
  const [analysisComplete, setAnalysisComplete] = useState(false)

  const updateMutation = useUpdateBrandProfile({ id: brandProfile.id })
  const { trackBrandProfileEdited } = useOnboardingTracking()

  const [sections, setSections] = useState<ProfileSection[]>([])
  const [editingSectionKey, setEditingSectionKey] = useState<string | null>(null)
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set())

  const { data: brandKitData } = useBrandKit(brandProfile.brand_kit.id, { cacheTime: 1000 * 60 * 5 })
  const { mutate: deleteBrandProfile } = useDeleteBrandProfile({ id: brandProfile.id })
  const { mutate: deleteBrandKitSource } = useDeleteBrandKitSource()

  const handleBack = useCallback(
    async (websiteScrapeSucceeded = true) => {
      setProfile('')
      if (!deleteOnBack) {
        onBack(websiteScrapeSucceeded)
        return
      }
      deleteBrandProfile()
      brandKitData?.brand_kit?.brand_kit_sources?.forEach((source) =>
        deleteBrandKitSource({ brandKitId: brandProfile.brand_kit.id, id: source.id }),
      )
      onBack(websiteScrapeSucceeded)
    },
    [
      deleteOnBack,
      deleteBrandProfile,
      brandProfile.brand_kit.id,
      brandKitData?.brand_kit?.brand_kit_sources,
      deleteBrandKitSource,
      onBack,
    ],
  )

  const topRef = useRef<HTMLDivElement | null>(null)
  const bottomRef = useRef<HTMLDivElement | null>(null)
  const contentRef = useRef<HTMLDivElement | null>(null)

  const handleAnalysisComplete = useCallback(
    async (data: Data) => {
      const { status } = data.brand_profile_analysis
      if (status === AIJobStatus.Successful) {
        await queryClient.refetchQueries(['workspaces', workspaceId, 'brandProfiles', 'detail', brandProfile.id])
        setRefreshProfile(true)
        setAnalysisComplete(true)
        return
      }

      if (status === AIJobStatus.Failed || status === AIJobStatus.Expired) {
        handleBack(false)
      }
    },
    [queryClient, workspaceId, brandProfile.id, handleBack],
  )

  const handleToggleSection = useCallback(
    (sectionTitle: string) => {
      if (!isMobile) return

      setCollapsedSections((prev) => {
        const next = new Set(prev)
        if (next.has(sectionTitle)) {
          next.delete(sectionTitle)
        } else {
          next.add(sectionTitle)
        }
        return next
      })
    },
    [isMobile],
  )

  const handleSectionUpdate = useCallback(
    async (sectionKey: string, newContent: string) => {
      if (!brandProfile?.id) return

      // Extract index from the key (format: "title-index")
      const lastDashIndex = sectionKey.lastIndexOf('-')
      const sectionIndex = parseInt(sectionKey.substring(lastDashIndex + 1), 10)

      const updatedSections = sections.map((section, idx) =>
        idx === sectionIndex ? { ...section, content: newContent } : section,
      )

      const originalSections = sections
      setSections(updatedSections)

      const fullMarkdown = reconstructMarkdown(updatedSections)

      try {
        const updatePayload = {
          profile: fullMarkdown,
        }

        await updateMutation.mutateAsync(updatePayload)
        trackBrandProfileEdited()
        setProfile(fullMarkdown)
      } catch (error) {
        setSections(originalSections)
        reportError(error)
        showErrorMessageToast('Failed to save changes. Please try again.')
      }
    },
    [sections, updateMutation, brandProfile?.id, trackBrandProfileEdited],
  )

  const { data } = usePollBrandProfileAnalysis(
    { id: analysisId, brandProfileId: brandProfile.id },
    { onSuccess: handleAnalysisComplete },
  )

  useEffect(() => {
    if (typeof brandProfile.profile === 'string' && brandProfile.profile.length > 0 && profile.length === 0) {
      setProfile(brandProfile.profile)
    }
  }, [brandProfile.profile, profile.length])

  useEffect(() => {
    if (
      refreshProfile &&
      typeof brandProfile.profile === 'string' &&
      brandProfile.profile.length > 0 &&
      brandProfile.profile !== profile
    ) {
      setProfile(brandProfile.profile)
      setRefreshProfile(false)
      setStreamingComplete(true)
    }
  }, [brandProfile.profile, refreshProfile, profile])

  useEffect(() => {
    const analysisJobId = data?.brand_profile_analysis?.job_id
    const isAnalysisComplete = data?.brand_profile_analysis?.status === AIJobStatus.Successful

    if (!analysisJobId || isAnalysisComplete) return
    if (brandProfile.profile && typeof brandProfile.profile === 'string' && brandProfile.profile.length > 0) {
      return
    }

    setProfile('')
    setStreamingComplete(false)
    fetchStream({
      jobId: analysisJobId,
      onParsedLine: (line: string) => {
        setProfile((stream) => stream + line)
        bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      },
      onLastLineParsed: () => {
        setStreamingComplete(true)
      },
      onError: (error) => {
        reportError(error)
        onBack(true)
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.brand_profile_analysis?.job_id, data?.brand_profile_analysis?.status, brandProfile.profile])

  useEffect(() => {
    if (profile.length > 0) {
      const parsedSections = parseProfileSections(profile)
      setSections(parsedSections)
    } else {
      setSections([])
    }
  }, [profile])

  const [streamingComplete, setStreamingComplete] = useState(false)

  useEffect(() => {
    if (isMobile && sections.length > 1 && streamingComplete) {
      const sectionsToCollapse = new Set(sections.slice(1).map((s) => s.title))
      setCollapsedSections(sectionsToCollapse)
    }
  }, [sections, isMobile, streamingComplete])

  useEffect(() => {
    if (isMobile && collapsedSections.size > 0 && streamingComplete && contentRef.current) {
      let scrollTriggered = false

      const findScrollableParent = (element: HTMLElement | null): HTMLElement | null => {
        let current = element
        while (current) {
          const computed = window.getComputedStyle(current)
          if (
            (computed.overflowY === 'scroll' || computed.overflowY === 'auto') &&
            current.scrollHeight > current.clientHeight
          ) {
            return current
          }
          current = current.parentElement
        }
        return null
      }

      const observer = new MutationObserver((mutations) => {
        if (mutations.some((m) => m.removedNodes.length > 0) && !scrollTriggered) {
          scrollTriggered = true
          setTimeout(() => {
            const scrollableParent = findScrollableParent(topRef.current)
            scrollableParent?.scrollTo({ top: 0, behavior: 'smooth' })
          }, 50)
        }
      })

      observer.observe(contentRef.current, { childList: true, subtree: true })
      return () => observer.disconnect()
    }
  }, [collapsedSections, isMobile, streamingComplete])

  const analysisStatus = data?.brand_profile_analysis.status
  const completedStates = [AIJobStatus.Successful, AIJobStatus.Failed, AIJobStatus.Expired]
  const hasFinished = analysisStatus && completedStates.includes(analysisStatus)

  const loadingElement = !analysisComplete ? (
    <LoadingText
      url={brandProfile.url}
      handleBack={handleBack}
      cancelOnError={false}
      finishedLoading={profile.length > 1}
    />
  ) : null

  if (hasFinished) {
    return (
      <StickyFooterProvider
        footer={
          <StickyFooter>
            <FooterButtons>
              {!isMobile && (
                <Button variant="tertiary" size="lg" onPress={() => handleBack()} aria-label="Back">
                  Back
                </Button>
              )}
              <Button
                size={isMobile ? 'xl' : 'lg'}
                onPress={() => onNext(profile)}
                isDisabled={
                  data?.brand_profile_analysis.status !== AIJobStatus.Successful || editingSectionKey !== null
                }
                aria-label="Looks good"
                fullWidth={isMobile}
              >
                Looks good
              </Button>
            </FooterButtons>
          </StickyFooter>
        }
      >
        <>
          <div className={Styles.container}>
            <div className={Styles.topSection}>
              <BrandProfileTitleWithEditing name={brandProfile.name} isSaving={false} loadingElement={loadingElement} />
            </div>
            <div className={Styles.form}>
              <div ref={topRef} />
              {sections.length > 0 && (
                <div className={Styles.brandProfileContent} ref={contentRef}>
                  {sections.map((section, index) => {
                    const sectionKey = `${section.title}-${index}`
                    return (
                      <EditableSection
                        key={sectionKey}
                        section={section}
                        sectionKey={sectionKey}
                        onSectionUpdate={handleSectionUpdate}
                        isUpdating={updateMutation.isLoading}
                        isEditing={editingSectionKey === sectionKey}
                        onEdit={() => setEditingSectionKey(sectionKey)}
                        onCancel={() => setEditingSectionKey(null)}
                        isDisabled={editingSectionKey !== null && editingSectionKey !== sectionKey}
                        sectionClassName={Styles.section}
                        headerClassName={Styles.sectionHeader}
                        actionsClassName={Styles.sectionActions}
                        editButtonClassName={Styles.editButton}
                        contentClassName={Styles.sectionContent}
                        editingClassName={Styles.editingContainer}
                        editorClassName={Styles.proseEditor}
                        isCollapsed={collapsedSections.has(section.title)}
                        onToggle={() => handleToggleSection(section.title)}
                      />
                    )
                  })}
                </div>
              )}
              <div ref={bottomRef} />
            </div>
          </div>
        </>
      </StickyFooterProvider>
    )
  }

  return (
    <StickyFooterProvider
      footer={
        <StickyFooter>
          {!isMobile && (
            <FooterButtons>
              <Button variant="tertiary" size="lg" onPress={() => handleBack()} aria-label="Back">
                Back
              </Button>
              <Button size="lg" isDisabled aria-label="Looks good">
                Looks good
              </Button>
            </FooterButtons>
          )}
        </StickyFooter>
      }
    >
      <div className={Styles.container}>
        <BrandProfileTitleWithEditing
          name={brandProfile.name}
          canEdit={false}
          isEditing={false}
          onEdit={() => {}}
          onSave={() => {}}
          onCancel={() => {}}
          isSaving={false}
          loadingElement={loadingElement}
        />
        <div className={Styles.form}>
          <div ref={topRef} />
          {sections.length > 0 && (
            <div className={Styles.brandProfileContent} ref={contentRef}>
              {sections.map((section, index) => {
                const sectionKey = `${section.title}-${index}`
                return (
                  <EditableSection
                    key={sectionKey}
                    section={section}
                    sectionKey={sectionKey}
                    onSectionUpdate={handleSectionUpdate}
                    isUpdating={updateMutation.isLoading}
                    isEditing={editingSectionKey === sectionKey}
                    onEdit={() => setEditingSectionKey(sectionKey)}
                    onCancel={() => setEditingSectionKey(null)}
                    isDisabled={editingSectionKey !== null && editingSectionKey !== sectionKey}
                    sectionClassName={Styles.section}
                    headerClassName={Styles.sectionHeader}
                    actionsClassName={Styles.sectionActions}
                    editButtonClassName={Styles.editButton}
                    contentClassName={Styles.sectionContent}
                    editingClassName={Styles.editingContainer}
                    editorClassName={Styles.proseEditor}
                  />
                )
              })}
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      </div>
    </StickyFooterProvider>
  )
}

const BrandProfileWithEditingFormikContainer = ({ brandProfile, ...rest }: BrandProfileProps) => (
  <Formik onSubmit={() => {}} enableReinitialize initialValues={{}}>
    <BrandProfileWithEditing brandProfile={brandProfile} {...rest} />
  </Formik>
)

const BrandProfileTitleWithEditing = ({
  name,
  canEdit,
  isEditing,
  onEdit,
  onSave,
  onCancel,
  isSaving,
  loadingElement,
}: {
  name?: string
  canEdit?: boolean
  isEditing?: boolean
  onEdit?: () => void
  onSave?: () => void
  onCancel?: () => void
  isSaving?: boolean
  loadingElement?: React.ReactNode
}) => {
  return (
    <div className={Styles.titleContainer}>
      <div className={Styles.brandProfileTitleContainer}>
        <Heading level={1}>{`${name ? makePossessiveSingular(name) : 'Building your'} Business Profile`}</Heading>
        {loadingElement}
      </div>
      {canEdit &&
        (isEditing ? (
          <div className={Styles.sectionActions}>
            <Button variant="editor" id="brand-profile-cancel" size="sm" onPress={onCancel} isDisabled={isSaving}>
              Cancel
            </Button>
            <Button variant="ghost" id="brand-profile-save" size="sm" onPress={onSave} isDisabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        ) : (
          <Button variant="ghost" size="sm" frontIcon={Edit1} onPress={onEdit}>
            Edit
          </Button>
        ))}
    </div>
  )
}

export { BrandProfileWithEditingFormikContainer as BrandProfileWithEditing }
